import type { Page } from './common';

/**
 * The filter of an `/explorar` trail (card #222, spec 036 §2).
 *
 * **This shape was not invented here.** It is the vocabulary
 * `kardume_urbano_site/src/content/trails.js` has carried since #162, and it is
 * - on purpose - the same vocabulary the storefront filters use, which is what
 * lets the "ver na vitrine" button open `/empresas` with the very same boxes
 * ticked instead of the trail being a dead end.
 *
 * Every field is nullable because a trail declares **only** what it cares
 * about: "Aberto agora" is `{ openNow: true }` and nothing else. `null` means
 * "don't care", never a neutral value.
 *
 * ## The two semantics, different on purpose
 *
 * - `amenities`: **ALL** required. Asking for two means needing both.
 * - `priceRanges`: **ANY** one matches. A store has a single range, so
 *   "all at once" would mean nothing.
 */
export interface TrailFilter {
  /** Amenity codes from the manageable catalog (#305). ALL required. */
  amenities?: string[] | null;
  /** Ranges 1..4. ANY one matches. */
  priceRanges?: number[] | null;
  /** "At least this rating" (0..5), never a band. */
  minRating?: number | null;
  /** Minimum cashback percentage declared by the store (0..100). */
  minCashback?: number | null;
  /**
   * Only what is open at visit time. **Only `true` filters** - the core turns
   * `false` into `null`, because `false` is "don't care" and never "only the
   * closed ones".
   */
  openNow?: boolean | null;
}

/** SEO block of a trail. Nested in the response; flat in the request. */
export interface TrailSeoDto {
  title: string | null;
  description: string | null;
  keywords: string[];
}

/**
 * A trail on the curation screen
 * (`GET /v1/backoffice/explore/trails`).
 *
 * `localityLabel` travels next to `localityId` so the table can draw the row
 * without a second request, exactly like `FeedDto`.
 *
 * `visible` is `active` **and** the window, resolved together at read time. It
 * is **not** the same as `active`: a trail whose window closed yesterday comes
 * back `active: true, visible: false`, and the screen has to show both - see
 * `describeTrailVisibility`.
 *
 * **There is no `merchantCount` here, by design.** Counting it per row would
 * sweep the catalog once per trail; the count is the preview endpoint, asked
 * for one trail at a time from the form.
 */
export interface BackofficeTrailDto {
  id: string;
  createdAt: string;
  updatedAt: string;
  /** The public address: `/explorar/<slug>`. */
  slug: string;
  name: string;
  tagline: string | null;
  icon: string | null;
  filter: TrailFilter;
  /** `null` = every city. */
  localityId: string | null;
  /** "Toledo, PR" - ready for the table. */
  localityLabel: string | null;
  startsAt: string | null;
  endsAt: string | null;
  sortOrder: number;
  active: boolean;
  seo: TrailSeoDto;
  /** Active **and** inside the window right now. */
  visible: boolean;
}

/**
 * Create and edit payload.
 *
 * **The asymmetry with `BackofficeTrailDto` is deliberate**: the request is
 * flat (`seoTitle`/`seoDescription`/`seoKeywords`) and the response is nested
 * (`seo: { title, description, keywords }`). Same design as
 * `FeedRequestDto` vs `FeedDto`; do not "fix" it.
 *
 * `slug` is optional and derived from the name by the server when blank - but
 * **only when blank**. Renaming a trail never moves its address:
 * `/explorar/pet-friendly` is indexed, and rewriting the slug whenever someone
 * touches the card text would turn every shared link into a 404.
 */
export interface TrailRequestDto {
  /** Required, max 80. */
  name: string;
  /** Required. A filter with no criteria at all is refused (`TRAIL_EMPTY_FILTER`). */
  filter: TrailFilter;
  /** Max 80. Blank = the server derives it from the name (creation only). */
  slug?: string | null;
  /** Max 160. */
  tagline?: string | null;
  /** Max 40. One of `TRAIL_ICON_OPTIONS` - see that constant. */
  icon?: string | null;
  /** `null` = every city. */
  localityId?: string | null;
  /** ISO-8601 instants; `null` means "no bound on that side". */
  startsAt?: string | null;
  endsAt?: string | null;
  sortOrder?: number;
  active?: boolean;
  /** Max 160. */
  seoTitle?: string | null;
  /** Max 320. */
  seoDescription?: string | null;
  /** Array in the request; `seo.keywords` in the response. */
  seoKeywords?: string[] | null;
}

/**
 * The preview question: "how many stores does this trail catch?"
 *
 * Body is the filter itself and not a saved trail id, because the question is
 * asked **before saving** - that is what the preview is for.
 */
export interface TrailPreviewRequestDto {
  filter: TrailFilter;
  localityId?: string | null;
}

/** The preview answer. One field, as spec 036 §4 froze it. */
export interface TrailPreviewDto {
  count: number;
}

export type TrailPage = Page<BackofficeTrailDto>;

/**
 * The publication floor: a trail with fewer than 2 stores is neither published
 * nor indexed.
 *
 * **It does not travel in the JSON, and the server does not block saving below
 * it.** The count changes on its own with every new store, and refusing would
 * stop curation from preparing the ground - so the number is shown and the
 * screen warns. It lives here because the site applies the same 2 in the hub,
 * the sitemap and the pre-renderer (`MIN_MERCHANTS`), and a screen that stayed
 * quiet would let someone create a trail that never appears and then open a
 * ticket saying the backoffice is broken.
 */
export const TRAIL_MIN_MERCHANTS = 2;

/**
 * Copy for the `errorCode`s the core returns when it refuses a trail.
 *
 * **Every key here was read in the core, not guessed from the neighbour's
 * convention** - six of them in `TrailFilters` and two in
 * `BackofficeDiscoveryTrailService`. That check is the whole point: a wrong key
 * breaks nothing visibly, it just makes `extractErrorMessage` fall through to
 * the raw server text, and the useful sentence never reaches the curator. That
 * silent defect is what happened in #218.
 *
 * `TRAIL_INVALID_FILTER` is here too even though the form can never produce it:
 * it means the stored JSON is unreadable (someone edited the row by hand), and
 * the curator needs to be told to rewrite the filter rather than to retry.
 *
 * 404 (unknown city or trail) is **not** here: the core answers it with
 * `NotFoundException`, which carries no `errorCode`.
 */
export const TRAIL_ERROR_MESSAGES: Record<string, string> = {
  TRAIL_UNKNOWN_AMENITY:
    'Uma das comodidades escolhidas saiu do catálogo. Abra a lista de novo e escolha entre as que aparecem.',
  TRAIL_INVALID_PRICE_RANGE: 'A faixa de preço precisa estar entre $ e $$$$.',
  TRAIL_INVALID_MIN_RATING: 'A nota mínima precisa estar entre 0 e 5.',
  TRAIL_INVALID_MIN_CASHBACK: 'O cashback mínimo precisa estar entre 0% e 100%.',
  TRAIL_EMPTY_FILTER:
    'A trilha precisa de ao menos um critério. Sem nenhum ela seria a vitrine inteira com outro nome.',
  TRAIL_INVALID_WINDOW: 'O fim da vigência precisa ser depois do início.',
  TRAIL_SLUG_IN_USE: 'Este endereço já pertence a outra trilha. Escolha outro.',
  TRAIL_INVALID_FILTER:
    'O filtro gravado nesta trilha está ilegível. Refaça a seleção e salve para corrigi-lo.',
};

/**
 * The icons a trail may carry.
 *
 * **A closed set, and not the `IconPicker` of the design system.** The site
 * resolves `trail.icon` through a fixed map in
 * `kardume_urbano_site/src/pages/Explorar/trailIcons.js`, and anything outside
 * it silently falls back to a compass. A free text field - or a picker that
 * emits lucide names like `PawPrint` - would produce a trail that looks fine in
 * the backoffice and generic on the site, which is the same "escolher, nunca
 * digitar" failure the amenity vocabulary avoids (spec 036 §5).
 *
 * The labels are what the curator recognises; the values are what the site
 * maps. Adding one here without adding it to `trailIcons.js` buys nothing.
 */
export const TRAIL_ICON_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'clock', label: 'Relógio - horário' },
  { value: 'paw', label: 'Pata - pets' },
  { value: 'wifi', label: 'Wi-Fi - trabalho' },
  { value: 'sun', label: 'Sol - ao ar livre' },
  { value: 'family', label: 'Família - com crianças' },
  { value: 'users', label: 'Pessoas - com amigos' },
  { value: 'wallet', label: 'Carteira - preço' },
  { value: 'accessibility', label: 'Acessibilidade' },
  { value: 'parking', label: 'Carro - estacionamento' },
  { value: 'star', label: 'Estrela - avaliação' },
];
